import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Shuffle, ListChecks, Save, Search, Tag, Plus, X, Calendar, ArrowUp, ArrowDown } from 'lucide-react';
import { mediaUrl } from '../utils/mediaUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OVERLAY_PRESETS = ['', 'NEW', 'MUST WATCH', 'FEATURED', 'TRENDING', 'EXCLUSIVE'];

export const FeaturedManager = () => {
  const [settings, setSettings] = useState({ mode: 'random', rotation_interval_seconds: 7 });
  const [queue, setQueue] = useState([]);
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddPicker, setShowAddPicker] = useState(false);
  const [editingOverlay, setEditingOverlay] = useState(null);
  const [editingDates, setEditingDates] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, queueRes, moviesRes] = await Promise.all([
        axios.get(`${API}/admin/featured-settings`, { withCredentials: true }),
        axios.get(`${API}/admin/featured-queue`, { withCredentials: true }),
        axios.get(`${API}/movies`, { withCredentials: true })
      ]);
      setSettings(settingsRes.data);
      setQueue(queueRes.data);
      setMovies(moviesRes.data);
    } catch (error) {
      toast.error('Failed to load featured settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/featured-settings`, {
        mode: settings.mode,
        movie_ids: [],
        rotation_interval_seconds: settings.rotation_interval_seconds
      }, { withCredentials: true });
      toast.success('Featured settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddToQueue = async (movieId) => {
    try {
      await axios.post(`${API}/admin/featured-queue`, {
        movie_id: movieId,
        priority: queue.length
      }, { withCredentials: true });
      toast.success('Added to featured queue');
      fetchData();
      setShowAddPicker(false);
      setSearchQuery('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to add');
    }
  };

  const handleRemoveFromQueue = async (queueId) => {
    if (!window.confirm('Remove this movie from the featured queue?')) return;
    try {
      await axios.delete(`${API}/admin/featured-queue/${queueId}`, { withCredentials: true });
      toast.success('Removed from queue');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const handleUpdateDates = async (queueId, startDate, endDate) => {
    const item = queue.find(q => q.queue_id === queueId);
    try {
      await axios.put(`${API}/admin/featured-queue/${queueId}`, {
        movie_id: item.movie_id,
        start_date: startDate || null,
        end_date: endDate || null,
        priority: item.priority
      }, { withCredentials: true });
      toast.success('Dates updated');
      fetchData();
      setEditingDates(null);
    } catch (error) {
      toast.error('Failed to update dates');
    }
  };

  const handleChangePriority = async (queueId, direction) => {
    const idx = queue.findIndex(q => q.queue_id === queueId);
    if (idx === -1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= queue.length) return;

    const current = queue[idx];
    const other = queue[swapIdx];

    try {
      await Promise.all([
        axios.put(`${API}/admin/featured-queue/${current.queue_id}`, {
          movie_id: current.movie_id,
          start_date: current.start_date || null,
          end_date: current.end_date || null,
          priority: other.priority
        }, { withCredentials: true }),
        axios.put(`${API}/admin/featured-queue/${other.queue_id}`, {
          movie_id: other.movie_id,
          start_date: other.start_date || null,
          end_date: other.end_date || null,
          priority: current.priority
        }, { withCredentials: true })
      ]);
      fetchData();
    } catch (error) {
      toast.error('Failed to reorder');
    }
  };

  const handleSaveOverlay = async (movieId, overlayLabel, overlayDescription) => {
    try {
      await axios.put(
        `${API}/admin/movies/${movieId}/overlay`,
        { overlay_label: overlayLabel, overlay_description: overlayDescription },
        { withCredentials: true }
      );
      toast.success('Overlay updated');
      setEditingOverlay(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update overlay');
    }
  };

  const handleQuickRandomize = async () => {
    if (!window.confirm('This will replace your queue with 5 random movies. Continue?')) return;
    try {
      // Delete existing queue
      await Promise.all(queue.map(q =>
        axios.delete(`${API}/admin/featured-queue/${q.queue_id}`, { withCredentials: true })
      ));
      // Add 5 random movies
      const shuffled = [...movies].sort(() => Math.random() - 0.5);
      const picks = shuffled.slice(0, Math.min(5, shuffled.length));
      for (let i = 0; i < picks.length; i++) {
        await axios.post(`${API}/admin/featured-queue`, {
          movie_id: picks[i].movie_id,
          priority: picks.length - i
        }, { withCredentials: true });
      }
      toast.success('Randomized featured queue with 5 movies');
      fetchData();
    } catch (error) {
      toast.error('Failed to randomize');
    }
  };

  const queueMovieIds = queue.map(q => q.movie_id);
  const availableMovies = movies.filter(m =>
    !queueMovieIds.includes(m.movie_id) &&
    (!searchQuery || m.title.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const labelColors = {
    'NEW': '#10B981', 'MUST WATCH': '#DC2626', 'FEATURED': '#3B82F6',
    'TRENDING': '#F0C14B', 'EXCLUSIVE': '#8B5CF6'
  };

  const isDateActive = (item) => {
    const now = new Date().toISOString();
    if (item.start_date && now < item.start_date) return false;
    if (item.end_date && now > item.end_date) return false;
    return true;
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 mx-auto" style={{ borderColor: '#3B82F6' }}></div>
      </div>
    );
  }

  return (
    <div className="rounded-xl p-6" style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52' }} data-testid="featured-manager">
      <div className="flex items-center gap-2 mb-6">
        <Sparkles size={24} style={{ color: '#F0C14B' }} />
        <h2 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>Featured Collection</h2>
      </div>

      {/* Mode Selection */}
      <div className="mb-8">
        <label className="block text-sm font-semibold mb-3 uppercase tracking-wider" style={{ color: '#8197A4' }}>Display Mode</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={() => setSettings({ ...settings, mode: 'random' })}
            className="p-5 rounded-xl text-left transition-all"
            style={{
              backgroundColor: settings.mode === 'random' ? 'rgba(59, 130, 246, 0.15)' : '#0F171E',
              border: `2px solid ${settings.mode === 'random' ? '#3B82F6' : '#2A3F52'}`
            }}
            data-testid="mode-random-button"
          >
            <div className="flex items-center gap-3 mb-2">
              <Shuffle size={20} style={{ color: settings.mode === 'random' ? '#3B82F6' : '#8197A4' }} />
              <span className="font-bold" style={{ color: '#FFFFFF' }}>Random Rotation</span>
              {settings.mode === 'random' && (
                <span className="ml-auto text-xs px-2 py-1 rounded" style={{ backgroundColor: '#3B82F6', color: 'white' }}>Active</span>
              )}
            </div>
            <p className="text-sm" style={{ color: '#8197A4' }}>Automatically rotate through all available movies</p>
          </button>

          <button
            onClick={() => setSettings({ ...settings, mode: 'manual' })}
            className="p-5 rounded-xl text-left transition-all"
            style={{
              backgroundColor: settings.mode === 'manual' ? 'rgba(59, 130, 246, 0.15)' : '#0F171E',
              border: `2px solid ${settings.mode === 'manual' ? '#3B82F6' : '#2A3F52'}`
            }}
            data-testid="mode-manual-button"
          >
            <div className="flex items-center gap-3 mb-2">
              <ListChecks size={20} style={{ color: settings.mode === 'manual' ? '#3B82F6' : '#8197A4' }} />
              <span className="font-bold" style={{ color: '#FFFFFF' }}>Manual Queue</span>
              {settings.mode === 'manual' && (
                <span className="ml-auto text-xs px-2 py-1 rounded" style={{ backgroundColor: '#3B82F6', color: 'white' }}>Active</span>
              )}
            </div>
            <p className="text-sm" style={{ color: '#8197A4' }}>Handpick movies with optional date ranges</p>
          </button>
        </div>
      </div>

      {/* Rotation Interval */}
      <div className="mb-6">
        <label className="block text-sm font-semibold mb-3 uppercase tracking-wider" style={{ color: '#8197A4' }}>Rotation Interval (seconds)</label>
        <div className="flex items-center gap-4">
          <input type="range" min="3" max="30" value={settings.rotation_interval_seconds}
            onChange={(e) => setSettings({ ...settings, rotation_interval_seconds: parseInt(e.target.value) })}
            className="flex-1" data-testid="rotation-interval-slider" />
          <span className="text-xl font-bold min-w-[80px]" style={{ color: '#3B82F6' }}>{settings.rotation_interval_seconds}s</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-8 pb-6" style={{ borderBottom: '1px solid #2A3F52' }}>
        <button onClick={handleSaveSettings} disabled={saving}
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-bold hover:scale-105"
          style={{ backgroundColor: '#1E3A8A', color: 'white', boxShadow: '0 4px 15px rgba(30, 58, 138, 0.35)' }}
          data-testid="save-featured-settings">
          <Save size={18} /> {saving ? 'Saving...' : 'Save Settings'}
        </button>
        <button onClick={handleQuickRandomize}
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-bold hover:scale-105"
          style={{ backgroundColor: 'rgba(240, 193, 75, 0.15)', color: '#F0C14B', border: '1px solid #F0C14B' }}
          data-testid="quick-randomize-button">
          <Shuffle size={18} /> Randomize Queue
        </button>
      </div>

      {/* Featured Queue */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="text-xl font-bold flex items-center gap-2" style={{ color: '#FFFFFF' }}>
            Featured Queue
            <span className="text-sm font-normal px-3 py-1 rounded-full" style={{ backgroundColor: '#0F171E', color: '#3B82F6' }}>
              {queue.length} movie{queue.length !== 1 ? 's' : ''}
            </span>
          </h3>
          <button onClick={() => setShowAddPicker(!showAddPicker)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold hover:scale-105"
            style={{ backgroundColor: '#1E3A8A', color: 'white' }}
            data-testid="add-to-queue-button">
            <Plus size={18} /> Add Movie
          </button>
        </div>

        {/* Add Movie Picker */}
        {showAddPicker && (
          <div className="mb-4 p-4 rounded-xl" style={{ backgroundColor: '#0F171E', border: '1px solid #3B82F6' }}>
            <div className="relative mb-3">
              <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search movies to add..."
                className="w-full pl-10 pr-4 py-2 rounded-lg outline-none"
                style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52', color: '#FFFFFF' }}
                data-testid="add-movie-search" autoFocus />
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: '#8197A4' }} />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {availableMovies.slice(0, 15).map(m => (
                <button key={m.movie_id} onClick={() => handleAddToQueue(m.movie_id)}
                  className="w-full flex items-center gap-3 p-2 rounded-lg text-left hover:bg-opacity-80"
                  style={{ backgroundColor: '#1A2632' }}
                  data-testid={`add-movie-${m.movie_id}`}>
                  <img src={mediaUrl(m.poster_path, API) || `https://picsum.photos/seed/${m.movie_id}/40/60`}
                    alt={m.title} className="w-8 h-12 object-cover rounded" />
                  <div className="flex-1">
                    <p className="font-semibold text-sm" style={{ color: '#FFFFFF' }}>{m.title}</p>
                    <p className="text-xs" style={{ color: '#8197A4' }}>{m.release_year} • {m.age_rating}</p>
                  </div>
                  <Plus size={16} style={{ color: '#3B82F6' }} />
                </button>
              ))}
              {availableMovies.length === 0 && (
                <p className="text-center py-4 text-sm" style={{ color: '#8197A4' }}>
                  {searchQuery ? 'No matching movies' : 'All movies already in queue'}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Queue List */}
        {queue.length === 0 ? (
          <div className="text-center py-12 rounded-xl" style={{ backgroundColor: '#0F171E', border: '1px dashed #2A3F52' }}>
            <ListChecks size={40} className="mx-auto mb-3" style={{ color: '#8197A4' }} />
            <p className="font-semibold mb-1" style={{ color: '#FFFFFF' }}>No movies in queue</p>
            <p className="text-sm" style={{ color: '#8197A4' }}>Add movies to the featured queue to display them on the home page</p>
          </div>
        ) : (
          <div className="space-y-3">
            {queue.map((item, idx) => {
              const movie = item.movie || {};
              const active = isDateActive(item);
              return (
                <div key={item.queue_id} className="p-4 rounded-lg"
                  style={{ backgroundColor: '#0F171E', border: `1px solid ${active ? '#3B82F6' : '#2A3F52'}`, opacity: active ? 1 : 0.6 }}
                  data-testid={`queue-item-${item.queue_id}`}>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex flex-col gap-1">
                      <button onClick={() => handleChangePriority(item.queue_id, 'up')} disabled={idx === 0}
                        className="p-1 rounded disabled:opacity-30" style={{ backgroundColor: '#1A2632', color: '#FFFFFF' }}>
                        <ArrowUp size={12} />
                      </button>
                      <button onClick={() => handleChangePriority(item.queue_id, 'down')} disabled={idx === queue.length - 1}
                        className="p-1 rounded disabled:opacity-30" style={{ backgroundColor: '#1A2632', color: '#FFFFFF' }}>
                        <ArrowDown size={12} />
                      </button>
                    </div>
                    <img src={mediaUrl(movie.poster_path, API) || `https://picsum.photos/seed/${item.movie_id}/50/75`}
                      alt={movie.title} className="w-12 h-16 object-cover rounded" />
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="font-semibold" style={{ color: '#FFFFFF' }}>{movie.title}</h4>
                        {active ? (
                          <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(16, 185, 129, 0.15)', color: '#10B981' }}>ACTIVE</span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(129, 151, 164, 0.15)', color: '#8197A4' }}>OUT OF DATE RANGE</span>
                        )}
                      </div>
                      <p className="text-xs" style={{ color: '#8197A4' }}>
                        {movie.release_year} • {movie.age_rating}
                        {(item.start_date || item.end_date) && (
                          <span className="ml-2">
                            📅 {item.start_date ? new Date(item.start_date).toLocaleDateString() : '∞'}
                            {' → '}
                            {item.end_date ? new Date(item.end_date).toLocaleDateString() : '∞'}
                          </span>
                        )}
                      </p>
                      {movie.overlay_label && (
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          <span className="text-xs px-2 py-0.5 rounded font-bold" style={{
                            backgroundColor: (labelColors[movie.overlay_label] || '#3B82F6') + '20',
                            color: labelColors[movie.overlay_label] || '#3B82F6'
                          }}>
                            <Tag size={10} className="inline mr-1" />
                            {movie.overlay_label}
                          </span>
                          {movie.overlay_description && (
                            <span className="text-xs" style={{ color: '#8197A4' }}>{movie.overlay_description}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => setEditingDates(editingDates === item.queue_id ? null : item.queue_id)}
                        className="px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-1"
                        style={{ backgroundColor: '#2A3F52', color: '#FFFFFF' }}
                        data-testid={`edit-dates-${item.queue_id}`}>
                        <Calendar size={14} /> Dates
                      </button>
                      <button onClick={() => setEditingOverlay(editingOverlay === item.movie_id ? null : item.movie_id)}
                        className="px-3 py-2 rounded-lg text-sm font-semibold"
                        style={{ backgroundColor: '#2A3F52', color: '#FFFFFF' }}
                        data-testid={`edit-overlay-${item.movie_id}`}>
                        Overlay
                      </button>
                      <button onClick={() => handleRemoveFromQueue(item.queue_id)}
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: 'rgba(220, 38, 38, 0.15)', color: '#EF4444' }}
                        data-testid={`remove-queue-${item.queue_id}`}>
                        <X size={16} />
                      </button>
                    </div>
                  </div>

                  {editingDates === item.queue_id && (
                    <DateEditor item={item} onSave={handleUpdateDates} onCancel={() => setEditingDates(null)} />
                  )}

                  {editingOverlay === item.movie_id && (
                    <OverlayEditor movie={movie} onSave={handleSaveOverlay} onCancel={() => setEditingOverlay(null)} />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const DateEditor = ({ item, onSave, onCancel }) => {
  const [start, setStart] = useState(item.start_date ? item.start_date.slice(0, 16) : '');
  const [end, setEnd] = useState(item.end_date ? item.end_date.slice(0, 16) : '');

  const inputStyle = { backgroundColor: '#1A2632', border: '1px solid #2A3F52', color: '#FFFFFF' };

  return (
    <div className="mt-4 pt-4 space-y-3" style={{ borderTop: '1px solid #2A3F52' }}>
      <p className="text-sm font-semibold" style={{ color: '#8197A4' }}>Set date range (leave empty for always active)</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: '#8197A4' }}>Start Date</label>
          <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)}
            className="w-full px-3 py-2 rounded-lg outline-none text-sm" style={inputStyle}
            data-testid={`start-date-${item.queue_id}`} />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: '#8197A4' }}>End Date</label>
          <input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)}
            className="w-full px-3 py-2 rounded-lg outline-none text-sm" style={inputStyle}
            data-testid={`end-date-${item.queue_id}`} />
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={() => onSave(item.queue_id, start ? new Date(start).toISOString() : '', end ? new Date(end).toISOString() : '')}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ backgroundColor: '#1E3A8A', color: 'white' }}
          data-testid={`save-dates-${item.queue_id}`}>
          Save Dates
        </button>
        <button onClick={() => onSave(item.queue_id, '', '')}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ backgroundColor: '#2A3F52', color: '#FFFFFF' }}>
          Clear
        </button>
        <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm" style={{ color: '#8197A4' }}>Cancel</button>
      </div>
    </div>
  );
};

const OverlayEditor = ({ movie, onSave, onCancel }) => {
  const [overlayLabel, setOverlayLabel] = useState(movie.overlay_label || '');
  const [overlayDescription, setOverlayDescription] = useState(movie.overlay_description || '');
  const [customLabel, setCustomLabel] = useState(!OVERLAY_PRESETS.includes(movie.overlay_label || ''));

  const labelColors = {
    'NEW': '#10B981', 'MUST WATCH': '#DC2626', 'FEATURED': '#3B82F6',
    'TRENDING': '#F0C14B', 'EXCLUSIVE': '#8B5CF6'
  };

  return (
    <div className="mt-4 pt-4 space-y-3" style={{ borderTop: '1px solid #2A3F52' }}>
      <div>
        <label className="block text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: '#8197A4' }}>Label</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {OVERLAY_PRESETS.map(preset => (
            <button key={preset || 'none'}
              onClick={() => { setOverlayLabel(preset); setCustomLabel(false); }}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold"
              style={{
                backgroundColor: overlayLabel === preset && !customLabel
                  ? (preset ? labelColors[preset] : '#3B82F6') : '#2A3F52',
                color: 'white'
              }}>
              {preset || 'None'}
            </button>
          ))}
          <button onClick={() => setCustomLabel(true)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold"
            style={{ backgroundColor: customLabel ? '#F0C14B' : '#2A3F52', color: customLabel ? '#0F171E' : 'white' }}>
            Custom
          </button>
        </div>
        {customLabel && (
          <input type="text" value={overlayLabel}
            onChange={(e) => setOverlayLabel(e.target.value.toUpperCase())}
            placeholder="Custom label"
            className="w-full px-3 py-2 rounded-lg outline-none text-sm"
            style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52', color: '#FFFFFF' }}
            maxLength={20} />
        )}
      </div>
      <div>
        <label className="block text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: '#8197A4' }}>Description</label>
        <input type="text" value={overlayDescription}
          onChange={(e) => setOverlayDescription(e.target.value)}
          placeholder="Short marketing description"
          className="w-full px-3 py-2 rounded-lg outline-none text-sm"
          style={{ backgroundColor: '#1A2632', border: '1px solid #2A3F52', color: '#FFFFFF' }}
          maxLength={100}
          data-testid={`overlay-description-${movie.movie_id}`} />
      </div>
      <div className="flex gap-2">
        <button onClick={() => onSave(movie.movie_id, overlayLabel, overlayDescription)}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ backgroundColor: '#1E3A8A', color: 'white' }}
          data-testid={`save-overlay-${movie.movie_id}`}>
          Save
        </button>
        <button onClick={() => onSave(movie.movie_id, '', '')}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ backgroundColor: '#2A3F52', color: '#FFFFFF' }}>
          Clear
        </button>
        <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm" style={{ color: '#8197A4' }}>Cancel</button>
      </div>
    </div>
  );
};
